import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';
import { bubEvents } from 'shared/events/bub_events';

import { AccessibleRadioButtonGroup } from 'javascripts/components/accessible_radio_button_group';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { Modal } from 'javascripts/components/modal';
import { ReactComponent as CheckIcon } from 'images/check-icon-colorless.svg';

import { useAuthenticityToken } from 'javascripts/hooks/use-authenticity-token';
import classNames from 'classnames';

import styles from 'stylesheets/components/audiobook/report_audiobook_modal';

export function ReportAudiobookModal(props) {
  const authenticityToken = useAuthenticityToken();
  const [reportOption, setReportOption] = useState('');
  const [message, setMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formOptions = [
    {
      label: 'The sound quality is poor',
      value: 'poor-sound-quality',
    },
    {
      label: 'The description is misleading',
      value: 'misleading-description',
    },
    {
      label: 'Sample or book content is missing or incorrect',
      value: 'missing-or-incorrect-content',
    },
    {
      label: 'I am having difficulty listening to this book',
      value: 'difficulty-listening',
    },
    {
      label: 'The content of this book is inappropriate/offensive',
      value: 'offensive-content',
    },
    {
      label: 'Other',
      value: 'other',
    },
  ];

  useEffect(() => {
    if (props.resetForm) {
      // clear out failure message so user can resubmit form
      setFailureMessage('');
    }
  }, [props.resetForm]);

  function onReportOptionChange(option) {
    setReportOption(option.value);
  }

  function onMessageChange(e) {
    setMessage(e.target.value);
  }

  function radioOption(radioProps) {
    return (
      <div
        alt={radioProps.label}
        aria-checked={radioProps.selected}
        className={styles.reportOption}
        key={radioProps.id}
        onClick={() => radioProps.onSelect(radioProps)}
        onKeyDown={radioProps.onKeyDown}
        ref={radioProps.radioRef}
        role='radio'
        tabIndex={radioProps.selected || radioProps.overrideIndex ? '0' : '-1'}
        value={radioProps.value}
      >
        <div className={classNames(styles.checkbox, { [styles.checked]: radioProps.selected })}>
          {radioProps.selected ? <CheckIcon className={styles.checkIcon} /> : null}
        </div>
        {radioProps.label}
      </div>
    );
  }

  function renderSuccessMessage() {
    return (
      <div className={styles.form}>
        <div className={styles.headerText}>Thank you!</div>
        <div className={styles.label}>Our team will review your feedback.</div>
        <div className={styles.closeButtonWrapper}>
          <ChirpButton
            onClick={props.onRequestClose}
            displayType='button'
            displayStyle='yellow'
            fullWidth
            label='Close'
          />
        </div>
      </div>
    );
  }

  function renderFailureMessage() {
    if (failureMessage) {
      return (
        <div className={styles.form}>
          <div className={styles.headerText}>Sorry, something has gone wrong!</div>
          <div className={styles.label}>{failureMessage}</div>
          <div className={styles.closeButtonWrapper}>
            <ChirpButton
              onClick={props.onRequestClose}
              displayType='button'
              displayStyle='yellow'
              fullWidth
              label='Close'
            />
          </div>
        </div>
      );
    }

    return null;
  }

  function handleFormSubmit(e) {
    e.preventDefault();

    bubEvents.track('Report audiobook', 'Clicked button', {
      label: 'Submit',
      properties: {
        audiobook_id: props.audiobook.id,
      },
    });

    setIsSubmitting(true);

    axios
      .post(
        '/report-audiobook-to-zendesk',
        {
          audiobook_id: props.audiobook.id,
          report_reason: reportOption,
          additional_details: message,
        },
        { headers: { 'X-CSRF-Token': authenticityToken } },
      )
      .then(response => {
        if (response && response.data.error === 'Rate limit exceeded.') {
          setFailureMessage(
            "You've exceeded the number of attempts to report an audiobook. Please try again later or contact the Chirp Support team for more assistance.",
          );
        } else {
          setShowSuccessMessage(true);
        }
        setIsSubmitting(false);
      })
      .catch(() => {
        setFailureMessage(
          'Please try filling out the form again, or contact the Chirp Support team for more assistance.',
        );
      });
  }

  function renderForm() {
    return (
      <form className={styles.form} onSubmit={handleFormSubmit} method='post' acceptCharset='UTF-8'>
        <input name='utf8' type='hidden' value='✓' />
        <div className={styles.headerText}>Report Audiobook</div>
        <AccessibleRadioButtonGroup
          selectedValue={reportOption}
          groupClass={styles.reportOptionGroup}
          label='Select a reason for reporting this audiobook'
          onSelect={onReportOptionChange}
          options={formOptions}
          RadioComponent={radioOption}
        />
        <div className={styles.label}>Please share any additional details.</div>
        <textarea
          maxLength={500}
          id='message'
          name='message'
          className={styles.message}
          value={message}
          onChange={onMessageChange}
        />
        <div className={styles.characterCount}>{`${message.length}/500`}</div>
        <ChirpButton
          onClick={handleFormSubmit}
          displayStyle='yellow'
          fullWidth
          isDisabled={!reportOption || isSubmitting}
          label='Submit'
        />
        <div className={styles.cancelButtonWrapper}>
          <ChirpButton onClick={props.onRequestClose} displayType='link' fullWidth label='Cancel' />
        </div>
      </form>
    );
  }

  function renderModalContent() {
    let modalContent = renderForm();
    if (showSuccessMessage) {
      modalContent = renderSuccessMessage();
    }
    if (failureMessage) {
      modalContent = renderFailureMessage();
    }
    return (
      <div className={styles.reportAudiobookModal} data-testid='report-audiobook-modal'>
        {modalContent}
      </div>
    );
  }

  return (
    <Modal appSelector='#audiobook-app' isOpen={props.isOpen} onRequestClose={props.onRequestClose}>
      {renderModalContent()}
    </Modal>
  );
}

ReportAudiobookModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  audiobook: ChirpPropShapes.audiobook.isRequired,
  resetForm: PropTypes.bool.isRequired,
};

ReportAudiobookModal.defaultProps = {
  isOpen: false,
};
